/**
 * Bus Service
 * Provides bus stop and route information near metro stations
 */

import { calculateDistance } from './nearbyPlaces';
import { CityId } from '../types/metro.types';

export interface BusStop {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  routes: string[];
  distanceMeters: number;
  type: 'bus_stop' | 'bus_terminal';
}

export interface BusRoute {
  id: string;
  number: string;
  name: string;
  from: string;
  to: string;
  frequency: string;
}

// Major bus stops/terminals near metro stations for each city
const CITY_BUS_STOPS: Record<string, BusStop[]> = {
  delhi: [
    { id: 'isbt-kashmere', name: 'ISBT Kashmere Gate', latitude: 28.6677, longitude: 77.2289, routes: ['DTC 181', 'DTC 212', 'DTC 473', 'DTC 729'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'isbt-anand-vihar', name: 'ISBT Anand Vihar', latitude: 28.6469, longitude: 77.3164, routes: ['DTC 473', 'DTC 534', 'DTC 337'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'isbt-sarai-kale-khan', name: 'ISBT Sarai Kale Khan', latitude: 28.5892, longitude: 77.2567, routes: ['DTC 405', 'DTC 423', 'DTC 502'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'cp-bus-stand', name: 'Connaught Place Bus Stand', latitude: 28.6315, longitude: 77.2167, routes: ['DTC 505', 'DTC 604', 'DTC 680'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'rajiv-chowk-bus', name: 'Rajiv Chowk Bus Stop', latitude: 28.6328, longitude: 77.2197, routes: ['DTC 604', 'DTC 505'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'ito-bus-stand', name: 'ITO Bus Stand', latitude: 28.6289, longitude: 77.2411, routes: ['DTC 423', 'DTC 534', 'DTC 181'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'nehru-place-bus', name: 'Nehru Place Terminal', latitude: 28.5494, longitude: 77.2531, routes: ['DTC 502', 'DTC 517', 'DTC 544'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'dwarka-bus', name: 'Dwarka Sector 21 Bus Stand', latitude: 28.5525, longitude: 77.0583, routes: ['DTC 764', 'DTC 773'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'noida-bus', name: 'Noida City Centre Bus Stand', latitude: 28.5744, longitude: 77.3567, routes: ['DTC 85', 'UP Roadways'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'central-sectt-bus', name: 'Central Secretariat Bus Stop', latitude: 28.6147, longitude: 77.2120, routes: ['DTC 604', 'DTC 505', 'DTC 680'], distanceMeters: 0, type: 'bus_stop' },
  ],
  mumbai: [
    { id: 'dadar-bus-depot', name: 'Dadar Bus Depot', latitude: 19.0178, longitude: 72.8478, routes: ['BEST 1', 'BEST 22', 'BEST 68'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'kurla-bus-depot', name: 'Kurla Bus Depot', latitude: 19.0728, longitude: 72.8794, routes: ['BEST 313', 'BEST 368', 'BEST 451'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'borivali-bus-depot', name: 'Borivali Bus Depot', latitude: 19.2297, longitude: 72.8564, routes: ['BEST 201', 'BEST 203', 'BEST 242'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'andheri-bus-station', name: 'Andheri Bus Station', latitude: 19.1136, longitude: 72.8697, routes: ['BEST 206', 'BEST 328', 'BEST 332'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'bandra-bus-stand', name: 'Bandra Bus Stand', latitude: 19.0544, longitude: 72.8402, routes: ['BEST 214', 'BEST 215', 'BEST 312'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'cst-bus-stand', name: 'CSMT Bus Stand', latitude: 18.9398, longitude: 72.8355, routes: ['BEST 1', 'BEST 3', 'BEST 6'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'thane-bus-depot', name: 'Thane Bus Depot', latitude: 19.1972, longitude: 72.9631, routes: ['TMT 1', 'TMT 35', 'TMT 88'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'ghatkopar-bus', name: 'Ghatkopar Bus Stand', latitude: 19.0869, longitude: 72.9086, routes: ['BEST 341', 'BEST 371', 'BEST 451'], distanceMeters: 0, type: 'bus_stop' },
  ],
  bangalore: [
    { id: 'majestic-bus-station', name: 'Kempegowda Bus Station (Majestic)', latitude: 12.9772, longitude: 77.5719, routes: ['BMTC 201', 'BMTC 500', 'BMTC V500'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'shivajinagar-bus', name: 'Shivajinagar Bus Station', latitude: 12.9857, longitude: 77.6058, routes: ['BMTC 300', 'BMTC 314', 'BMTC 401'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'kbs-satellite', name: 'KBS Satellite Bus Station', latitude: 12.9239, longitude: 77.5869, routes: ['BMTC 210', 'BMTC 600'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'yelahanka-bus', name: 'Yelahanka Bus Stand', latitude: 13.1007, longitude: 77.5963, routes: ['BMTC 290', 'BMTC 292', 'BMTC 294'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'electronic-city-bus', name: 'Electronic City Bus Stop', latitude: 12.8445, longitude: 77.6769, routes: ['BMTC 500C', 'BMTC 500CA'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'whitefield-bus', name: 'Whitefield Bus Stand', latitude: 12.9698, longitude: 77.7500, routes: ['BMTC 500J', 'BMTC 335E'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'mg-road-bus', name: 'MG Road Bus Stop', latitude: 12.9757, longitude: 77.6061, routes: ['BMTC 201', 'BMTC 314'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'indiranagar-bus', name: 'Indiranagar Bus Stop', latitude: 12.9784, longitude: 77.6408, routes: ['BMTC 201R', 'BMTC 500D'], distanceMeters: 0, type: 'bus_stop' },
  ],
  chennai: [
    { id: 'cmbt', name: 'CMBT (Chennai Mofussil Bus Terminus)', latitude: 13.0694, longitude: 80.2028, routes: ['MTC 15', 'MTC 70', 'MTC 170'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'broadway-bus', name: 'Broadway Bus Terminus', latitude: 13.0886, longitude: 80.2842, routes: ['MTC 5', 'MTC 21', 'MTC 27'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'koyambedu-bus', name: 'Koyambedu Bus Stand', latitude: 13.0694, longitude: 80.2028, routes: ['MTC 15', 'MTC 154', 'MTC 170'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 't-nagar-bus', name: 'T. Nagar Bus Stand', latitude: 13.0358, longitude: 80.2350, routes: ['MTC 17', 'MTC 47', 'MTC 23C'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'central-bus', name: 'Central Railway Station Bus Stop', latitude: 13.0836, longitude: 80.2754, routes: ['MTC 1', 'MTC 17B', 'MTC 27'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'guindy-bus', name: 'Guindy Bus Terminus', latitude: 13.0094, longitude: 80.2127, routes: ['MTC 52', 'MTC 70', 'MTC 154'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'tambaram-bus', name: 'Tambaram Bus Stand', latitude: 12.9249, longitude: 80.1000, routes: ['MTC 52A', 'MTC 154A'], distanceMeters: 0, type: 'bus_stop' },
  ],
  kolkata: [
    { id: 'esplanade-bus', name: 'Esplanade Bus Terminus', latitude: 22.5625, longitude: 88.3511, routes: ['CSTC S12', 'CSTC AC2', 'SBSTC'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'howrah-bus', name: 'Howrah Bus Stand', latitude: 22.5852, longitude: 88.3422, routes: ['CSTC 6', 'CSTC 9', 'CSTC 12'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'babughat-bus', name: 'Babughat Bus Terminus', latitude: 22.5703, longitude: 88.3389, routes: ['CSTC 5', 'CSTC 9', 'SBSTC'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'sealdah-bus', name: 'Sealdah Bus Stop', latitude: 22.5664, longitude: 88.3697, routes: ['CSTC 12', 'CSTC 30'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'salt-lake-bus', name: 'Salt Lake Bus Stand', latitude: 22.5756, longitude: 88.4128, routes: ['CSTC AC9', 'CSTC S32'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'new-town-bus', name: 'New Town Bus Terminus', latitude: 22.5958, longitude: 88.4681, routes: ['CSTC AC6', 'CSTC AC12'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'garia-bus', name: 'Garia Bus Stand', latitude: 22.4631, longitude: 88.3822, routes: ['CSTC 6', 'CSTC 9'], distanceMeters: 0, type: 'bus_stop' },
  ],
  hyderabad: [
    { id: 'mgbs', name: 'Mahatma Gandhi Bus Station (MGBS)', latitude: 17.3784, longitude: 78.4867, routes: ['TSRTC 5K', 'TSRTC 10K', 'TSRTC 65'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'jbs', name: 'Jubilee Bus Station (JBS)', latitude: 17.4417, longitude: 78.4983, routes: ['TSRTC 1', 'TSRTC 10H', 'TSRTC 20'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'secunderabad-bus', name: 'Secunderabad Bus Stand', latitude: 17.4344, longitude: 78.5013, routes: ['TSRTC 1', 'TSRTC 47', 'TSRTC 127'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'lb-nagar-bus', name: 'LB Nagar Bus Stand', latitude: 17.3483, longitude: 78.5522, routes: ['TSRTC 65', 'TSRTC 107'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'hitech-city-bus', name: 'HITEC City Bus Stand', latitude: 17.4435, longitude: 78.3772, routes: ['TSRTC 127K', 'TSRTC 229K'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'ameerpet-bus', name: 'Ameerpet Bus Stop', latitude: 17.4375, longitude: 78.4483, routes: ['TSRTC 5K', 'TSRTC 10H', 'TSRTC 20'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'charminar-bus', name: 'Charminar Bus Stand', latitude: 17.3616, longitude: 78.4747, routes: ['TSRTC 65', 'TSRTC 142'], distanceMeters: 0, type: 'bus_stop' },
  ],
  kochi: [
    { id: 'vytilla-bus', name: 'Vytilla Mobility Hub', latitude: 9.9717, longitude: 76.3186, routes: ['KSRTC', 'Private Buses'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'ernakulam-bus', name: 'Ernakulam Bus Stand', latitude: 9.9816, longitude: 76.2883, routes: ['KSRTC 1', 'KSRTC 15'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'aluva-bus', name: 'Aluva Bus Stand', latitude: 10.1074, longitude: 76.3519, routes: ['KSRTC 10', 'KSRTC 12'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'mg-road-kochi-bus', name: 'MG Road Bus Stop', latitude: 9.9710, longitude: 76.2833, routes: ['KSRTC 1', 'KSRTC 3'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'fort-kochi-bus', name: 'Fort Kochi Bus Stand', latitude: 9.9658, longitude: 76.2420, routes: ['KSRTC Ferry', 'Local Buses'], distanceMeters: 0, type: 'bus_stop' },
  ],
  lucknow: [
    { id: 'charbagh-bus', name: 'Charbagh Bus Stand', latitude: 26.8333, longitude: 80.9211, routes: ['UPSRTC 1', 'UPSRTC 5', 'City Bus'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'alambagh-bus', name: 'Alambagh Bus Terminal', latitude: 26.8144, longitude: 80.9117, routes: ['UPSRTC 3', 'UPSRTC 10'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'kaiserbagh-bus', name: 'Kaiserbagh Bus Stand', latitude: 26.8547, longitude: 80.9356, routes: ['City Bus 1', 'City Bus 5'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'hazratganj-bus', name: 'Hazratganj Bus Stop', latitude: 26.8520, longitude: 80.9462, routes: ['City Bus 2', 'City Bus 7'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'gomti-nagar-bus', name: 'Gomti Nagar Bus Stand', latitude: 26.8558, longitude: 80.9917, routes: ['City Bus 10', 'City Bus 12'], distanceMeters: 0, type: 'bus_stop' },
  ],
  jaipur: [
    { id: 'sindhi-camp-bus', name: 'Sindhi Camp Bus Stand', latitude: 26.9167, longitude: 75.7833, routes: ['RSRTC 1', 'RSRTC 5', 'City Bus'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'narayan-singh-bus', name: 'Narayan Singh Circle Bus Stand', latitude: 26.9200, longitude: 75.7950, routes: ['City Bus 1', 'City Bus 3'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'badi-chaupar-bus', name: 'Badi Chaupar Bus Stop', latitude: 26.9239, longitude: 75.8267, routes: ['City Bus 2', 'City Bus 5'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'mansarovar-bus', name: 'Mansarovar Bus Stand', latitude: 26.8681, longitude: 75.7553, routes: ['City Bus 10', 'RSRTC 15'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'raja-park-bus', name: 'Raja Park Bus Stand', latitude: 26.8928, longitude: 75.8089, routes: ['City Bus 3', 'City Bus 7'], distanceMeters: 0, type: 'bus_stop' },
  ],
  nagpur: [
    { id: 'ganeshpeth-bus', name: 'Ganeshpeth Bus Stand', latitude: 21.1533, longitude: 79.0867, routes: ['MSRTC', 'City Bus'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'mor-bhavan-bus', name: 'Mor Bhavan Bus Stand', latitude: 21.1500, longitude: 79.0833, routes: ['Aapli Bus 1', 'Aapli Bus 5'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'variety-square-bus', name: 'Variety Square Bus Stop', latitude: 21.1417, longitude: 79.0833, routes: ['Aapli Bus 3', 'Aapli Bus 7'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'sitabuldi-bus', name: 'Sitabuldi Bus Stand', latitude: 21.1440, longitude: 79.0840, routes: ['Aapli Bus 1', 'Aapli Bus 2'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'railway-station-bus', name: 'Railway Station Bus Stop', latitude: 21.1458, longitude: 79.0833, routes: ['Aapli Bus 5', 'MSRTC'], distanceMeters: 0, type: 'bus_stop' },
  ],
  ahmedabad: [
    { id: 'geeta-mandir-bus', name: 'Geeta Mandir Bus Station', latitude: 23.0167, longitude: 72.5833, routes: ['AMTS 1', 'AMTS 15', 'BRTS 1'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'lal-darwaja-bus', name: 'Lal Darwaja Bus Stand', latitude: 23.0251, longitude: 72.5810, routes: ['AMTS 5', 'AMTS 10', 'BRTS 3'], distanceMeters: 0, type: 'bus_terminal' },
    { id: 'paldi-bus', name: 'Paldi Bus Stand', latitude: 23.0083, longitude: 72.5583, routes: ['AMTS 12', 'AMTS 21'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'kalupur-bus', name: 'Kalupur Railway Station Bus Stop', latitude: 23.0333, longitude: 72.6083, routes: ['AMTS 1', 'AMTS 3', 'BRTS 5'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'sg-highway-bus', name: 'SG Highway BRTS', latitude: 23.0333, longitude: 72.5083, routes: ['BRTS 1', 'BRTS 2', 'BRTS 5'], distanceMeters: 0, type: 'bus_stop' },
    { id: 'gandhinagar-bus', name: 'Gandhinagar Bus Station', latitude: 23.2156, longitude: 72.6369, routes: ['GSRTC 1', 'GSRTC 5'], distanceMeters: 0, type: 'bus_terminal' },
  ],
};

// Major bus routes per city
const CITY_BUS_ROUTES: Record<string, BusRoute[]> = {
  delhi: [
    { id: 'dtc-505', number: '505', name: 'CP to Jahangirpuri', from: 'Connaught Place', to: 'Jahangirpuri', frequency: 'Every 10 mins' },
    { id: 'dtc-604', number: '604', name: 'ISBT to Mehrauli', from: 'ISBT Kashmere Gate', to: 'Mehrauli', frequency: 'Every 15 mins' },
    { id: 'dtc-181', number: '181', name: 'ISBT to Noida', from: 'ISBT Kashmere Gate', to: 'Noida', frequency: 'Every 12 mins' },
    { id: 'dtc-423', number: '423', name: 'Sarai Kale Khan to Dwarka', from: 'Sarai Kale Khan', to: 'Dwarka', frequency: 'Every 20 mins' },
  ],
  mumbai: [
    { id: 'best-1', number: '1', name: 'CSMT to Colaba', from: 'CSMT', to: 'Colaba', frequency: 'Every 5 mins' },
    { id: 'best-22', number: '22', name: 'Dadar to Kurla', from: 'Dadar', to: 'Kurla', frequency: 'Every 8 mins' },
    { id: 'best-313', number: '313', name: 'Kurla to Andheri', from: 'Kurla', to: 'Andheri', frequency: 'Every 10 mins' },
  ],
  bangalore: [
    { id: 'bmtc-500', number: '500', name: 'Majestic to Electronic City', from: 'Majestic', to: 'Electronic City', frequency: 'Every 10 mins' },
    { id: 'bmtc-201', number: '201', name: 'Majestic to MG Road', from: 'Majestic', to: 'MG Road', frequency: 'Every 8 mins' },
    { id: 'bmtc-v500', number: 'V500', name: 'Volvo AC - Majestic to Whitefield', from: 'Majestic', to: 'Whitefield', frequency: 'Every 15 mins' },
  ],
  chennai: [
    { id: 'mtc-15', number: '15', name: 'CMBT to T. Nagar', from: 'CMBT', to: 'T. Nagar', frequency: 'Every 10 mins' },
    { id: 'mtc-70', number: '70', name: 'CMBT to Guindy', from: 'CMBT', to: 'Guindy', frequency: 'Every 12 mins' },
    { id: 'mtc-27', number: '27', name: 'Broadway to Central', from: 'Broadway', to: 'Central', frequency: 'Every 8 mins' },
  ],
  kolkata: [
    { id: 'cstc-s12', number: 'S12', name: 'Esplanade to Salt Lake', from: 'Esplanade', to: 'Salt Lake', frequency: 'Every 15 mins' },
    { id: 'cstc-6', number: '6', name: 'Howrah to Garia', from: 'Howrah', to: 'Garia', frequency: 'Every 10 mins' },
  ],
  hyderabad: [
    { id: 'tsrtc-5k', number: '5K', name: 'MGBS to Kukatpally', from: 'MGBS', to: 'Kukatpally', frequency: 'Every 10 mins' },
    { id: 'tsrtc-127', number: '127', name: 'Secunderabad to HITEC City', from: 'Secunderabad', to: 'HITEC City', frequency: 'Every 12 mins' },
  ],
  kochi: [
    { id: 'ksrtc-1', number: '1', name: 'Ernakulam to Fort Kochi', from: 'Ernakulam', to: 'Fort Kochi', frequency: 'Every 20 mins' },
  ],
  lucknow: [
    { id: 'upsrtc-1', number: '1', name: 'Charbagh to Gomti Nagar', from: 'Charbagh', to: 'Gomti Nagar', frequency: 'Every 15 mins' },
  ],
  jaipur: [
    { id: 'rsrtc-1', number: '1', name: 'Sindhi Camp to Badi Chaupar', from: 'Sindhi Camp', to: 'Badi Chaupar', frequency: 'Every 12 mins' },
  ],
  nagpur: [
    { id: 'aapli-1', number: '1', name: 'Railway Station to Mor Bhavan', from: 'Railway Station', to: 'Mor Bhavan', frequency: 'Every 15 mins' },
  ],
  ahmedabad: [
    { id: 'brts-1', number: 'BRTS 1', name: 'Geeta Mandir to SG Highway', from: 'Geeta Mandir', to: 'SG Highway', frequency: 'Every 5 mins' },
    { id: 'amts-1', number: 'AMTS 1', name: 'Lal Darwaja to Kalupur', from: 'Lal Darwaja', to: 'Kalupur', frequency: 'Every 10 mins' },
  ],
};

// Cities with bus data available
export const CITIES_WITH_BUS_DATA: CityId[] = [
  'delhi', 'mumbai', 'bangalore', 'chennai', 'kolkata',
  'hyderabad', 'kochi', 'lucknow', 'jaipur', 'nagpur', 'ahmedabad'
];

/**
 * Get all bus stops for a city
 */
export const getAllBusStops = (cityId: string): BusStop[] => {
  return CITY_BUS_STOPS[cityId] || [];
};

/**
 * Get all bus routes for a city
 */
export const getAllBusRoutes = (cityId: string): BusRoute[] => {
  return CITY_BUS_ROUTES[cityId] || [];
};

/**
 * Get nearby bus stops from a station
 */
export const getNearbyBusStops = (
  stationLat: number,
  stationLon: number,
  cityId: string,
  maxDistanceMeters: number = 5000
): BusStop[] => {
  const cityBusStops = getAllBusStops(cityId);

  const nearbyStops = cityBusStops
    .map(stop => ({
      ...stop,
      distanceMeters: calculateDistance(stationLat, stationLon, stop.latitude, stop.longitude),
    }))
    .filter(stop => stop.distanceMeters <= maxDistanceMeters)
    .sort((a, b) => a.distanceMeters - b.distanceMeters);

  return nearbyStops;
};

/**
 * Format bus routes for display
 */
export const formatBusRoutes = (routes: string[]): string => {
  if (routes.length === 0) return 'No routes available';
  if (routes.length <= 3) return routes.join(', ');
  return `${routes.slice(0, 3).join(', ')} +${routes.length - 3} more`;
};

/**
 * Check if a city has bus data
 */
export const hasCityBusData = (cityId: string): boolean => {
  return CITIES_WITH_BUS_DATA.includes(cityId as CityId);
};
